import { elapsedMinutes, elapsedText, money, whenText } from '../lib/money'
import type { Order, DiningTable } from '../lib/db'
import { TableIcon } from './TableIcon'

/**
 * Bảng màu bàn. Dùng token theo chủ đề chứ không phải mã hex cố định — quán mở
 * lúc 5 giờ sáng và 10 giờ đêm, máy tự đổi sang nền tối thì màu bàn phải đi theo.
 */
export const TABLE_COLORS: Record<string, { bg: string; line: string; ink: string }> = {
  slate: { bg: 'var(--surface)', line: 'var(--line)', ink: 'var(--ink-2)' },
  green: { bg: 'var(--brand-soft)', line: 'var(--brand)', ink: 'var(--brand-ink)' },
  teal: { bg: 'var(--teal-soft)', line: 'var(--teal)', ink: 'var(--teal-ink)' },
  blue: { bg: 'var(--blue-soft)', line: 'var(--blue)', ink: 'var(--blue-ink)' },
  violet: { bg: 'var(--violet-soft)', line: 'var(--violet)', ink: 'var(--violet-ink)' },
  amber: { bg: 'var(--amber-soft)', line: 'var(--amber)', ink: 'var(--amber)' },
  clay: { bg: 'var(--clay-soft)', line: 'var(--clay)', ink: 'var(--clay)' },
}

export const TABLE_COLOR_LABELS: Record<string, string> = {
  slate: 'Trắng',
  green: 'Xanh lá',
  teal: 'Xanh ngọc',
  blue: 'Xanh dương',
  violet: 'Tím',
  amber: 'Vàng',
  clay: 'Đỏ gạch',
}

/** Bàn ngồi quá lâu thì đổi sang cảnh báo — quán đông, cần biết bàn nào sắp nên nhắc. */
const LONG_SIT_MINUTES = 90

interface Props {
  table: DiningTable
  order: Order | undefined
  /** Mốc thời gian truyền từ ngoài để mọi ô bàn nhích đồng hồ cùng một nhịp. */
  now: number
  editing?: boolean
  selected?: boolean
  onClick?: () => void
}

export function TableCard({ table, order, now, editing, selected, onClick }: Props) {
  const busy = !!order && order.status === 'Open'
  const mins = busy ? elapsedMinutes(order.openedAt, now) : 0
  const tooLong = busy && mins >= LONG_SIT_MINUTES

  const reservedSoon =
    !busy && table.reservedAt && new Date(table.reservedAt).getTime() > now - 30 * 60_000

  const palette = TABLE_COLORS[table.color ?? 'slate'] ?? TABLE_COLORS.slate!

  const style = busy
    ? { background: 'var(--amber-soft)', borderColor: tooLong ? 'var(--clay)' : 'var(--amber)' }
    : reservedSoon
      ? { background: 'var(--violet-soft)', borderColor: 'var(--violet)' }
      : { background: palette.bg, borderColor: palette.line }

  const cls = [
    'table-card',
    busy && 'is-busy',
    tooLong && 'is-long',
    reservedSoon && 'is-reserved',
    editing && 'is-editing',
    selected && 'is-selected',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={cls}
      style={style}
      onClick={onClick}
      aria-label={
        busy
          ? `${table.name}, đang có khách, ${money(order.total)} đồng, ngồi ${elapsedText(order.openedAt, now)}`
          : reservedSoon
            ? `${table.name}, đã đặt trước ${whenText(table.reservedAt!, new Date(now))}`
            : `${table.name}, trống`
      }
    >
      <div className="table-head">
        <TableIcon icon={table.icon} className="table-icon" />
        <span className="table-name">{table.name}</span>
        {reservedSoon && (
          <span className="table-flag" aria-hidden="true" title="Đã đặt trước">
            ⏰
          </span>
        )}
      </div>

      {busy ? (
        <div className="table-foot">
          <div className="table-amount num">{money(order.total)}</div>
          <div className={`table-time${tooLong ? ' warn' : ''}`}>
            <span aria-hidden="true">🕐</span> {elapsedText(order.openedAt, now)}
          </div>
        </div>
      ) : reservedSoon ? (
        <div className="table-foot">
          <div className="table-reserved">{whenText(table.reservedAt!, new Date(now))}</div>
          <div className="table-sub">
            {table.reservedName ?? 'Khách đặt'}
            {table.reservedPartySize > 0 ? ` · ${table.reservedPartySize} người` : ''}
          </div>
        </div>
      ) : (
        <div className="table-foot">
          <div className="table-sub">{table.seats > 0 ? `${table.seats} chỗ` : 'Mang về'}</div>
        </div>
      )}
    </button>
  )
}
